import { FormFieldWithErrors } from '../../components/FormFieldWithErrors'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { useCallback } from 'react'
import { useNavigate, createFileRoute } from '@tanstack/react-router'

import { Spinner } from '../../components'
import { useSession } from '../../context/session'

type FormData = {
  email: string
  password: string
}

const LoginPage = () => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormData>({ mode: 'all' })
  const navigate = useNavigate()
  const { login } = useSession()

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async ({ email, password }) => {
      clearErrors()
      try {
        await login(email, password)
        navigate({ to: '/' })
      } catch (e) {
        console.log(e)
        setError('root.login', { message: (e as Error).message })
      }
    },
    [clearErrors, login, navigate, setError],
  )

  return (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <h4 className="mb-2 text-xl font-bold text-gray-800">Login</h4>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="xl:w-1/3 lg:w-1/2 md:w-2/3 flex flex-col gap-2"
      >
        <FormFieldWithErrors
          label="Email"
          type="email"
          registerProps={register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' },
          })}
          error={errors.email}
        />
        <FormFieldWithErrors
          label="Password"
          type="password"
          registerProps={register('password', { required: true })}
          error={errors.password}
        />
        {errors.root?.login && (
          <div className="rounded-md border bg-orange-200 p-2 text-xs text-orange-800 flex align-right">
            {errors.root.login.message}
          </div>
        )}
        {!errors.root?.login && <div className="h-[34px]" />}
        <button
          className="rounded border-2 border-serblue-light bg-white py-2 px-2 text-center"
          disabled={!isValid || isSubmitting}
          type="submit"
        >
          {!isSubmitting && 'Login'}
          {isSubmitting && <Spinner />}
        </button>
      </form>
    </div>
  )
}

export const Route = createFileRoute('/auth/login')({
  component: LoginPage,
})
